import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Briefcase, X } from 'lucide-react';
import type { ProjectOption } from '@/lib/api/projectAnalysis';

interface ProjectSearchSelectProps {
  projects: ProjectOption[];
  value: number | '';
  onChange: (projectId: number | '', project?: ProjectOption) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
}

const ProjectSearchSelect: React.FC<ProjectSearchSelectProps> = ({
  projects,
  value,
  onChange,
  label = 'پروژه',
  placeholder = 'جستجو: نام، شماره قرارداد، وضعیت...',
  required = false,
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => (value !== '' ? projects.find((p) => p.id === value) : undefined),
    [projects, value]
  );

  useEffect(() => {
    if (selected) setSearch(selected.original_name);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search.trim()) return projects;
    const q = search.trim().toLowerCase();
    return projects.filter(
      (p) =>
        p.original_name.toLowerCase().includes(q) ||
        (p.name?.toLowerCase().includes(q) ?? false) ||
        (p.contract_number?.toLowerCase().includes(q) ?? false) ||
        p.status.toLowerCase().includes(q) ||
        (p.initial_estimate && String(p.initial_estimate).includes(q))
    );
  }, [projects, search]);

  useEffect(() => {
    if (open) {
      setHighlightedIndex(0);
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [open, search, filtered.length]);

  const handleSelect = useCallback(
    (p: ProjectOption) => {
      onChange(p.id, p);
      setSearch(p.original_name);
      setOpen(false);
    },
    [onChange]
  );

  const handleClear = () => {
    onChange('');
    setSearch('');
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && filtered[highlightedIndex]) {
      e.preventDefault();
      handleSelect(filtered[highlightedIndex]);
    }
  };

  const displayValue = search || selected?.original_name || '';

  return (
    <div className="relative">
      {label && (
        <label className="block text-xs font-bold text-slate-600 mb-1">
          {label}
          {required && ' *'}
        </label>
      )}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!e.target.value) onChange('');
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pr-9 pl-10 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 outline-none text-right bg-white"
          autoComplete="off"
        />
        {value !== '' ? (
          <button
            type="button"
            onClick={handleClear}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
            aria-label="پاک کردن"
          >
            <X size={16} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
            tabIndex={-1}
          >
            <ChevronDown size={16} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
        )}
      </div>

      {open && (
        <div
          ref={listRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg"
        >
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500 text-center">پروژه‌ای یافت نشد</div>
          ) : (
            filtered.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(p)}
                className={`w-full text-right px-3 py-2.5 flex items-start gap-2 border-b border-slate-50 last:border-0 hover:bg-emerald-50 transition-colors ${
                  i === highlightedIndex ? 'bg-emerald-50' : ''
                } ${value === p.id ? 'bg-emerald-50/70' : ''}`}
              >
                <Briefcase size={16} className="text-emerald-700 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{p.original_name}</div>
                  <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                    {p.contract_number && <span>قرارداد: {p.contract_number}</span>}
                    <span>{p.status}</span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectSearchSelect;
